import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const POSITION_GROUPS = [
  { label: 'Maalivahdit', match: (p) => ['G', 'GOALIE', 'GOALKEEPER'].includes((p || '').trim().toUpperCase()) },
  { label: 'Puolustajat', match: (p) => ['D', 'DEFENSE', 'DEFENSEMAN'].includes((p || '').trim().toUpperCase()) },
  { label: 'Hyökkääjät', match: (p) => ['F', 'FORWARD', 'LW', 'RW', 'C', 'CENTER'].includes((p || '').trim().toUpperCase()) },
];

function RosterByPosition({ players }) {
  return (
    <div className="roster-by-position">
      {POSITION_GROUPS.map((group) => {
        const groupPlayers = players.filter((p) => group.match(p.position));
        if (groupPlayers.length === 0) return null;
        return (
          <div key={group.label} className="roster-group">
            <h4 className="roster-group-label">{group.label}</h4>
            <ul className="team-players-list">
              {groupPlayers.map((player) => (
                <li key={player.id}>
                  {player.number ? <span className="player-number">#{player.number}</span> : null}
                  {player.name}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function Match({ match, back, socket, players, teams, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [goals, setGoals] = useState([]);
  const [penalties, setPenalties] = useState([]);
  const [newGoal, setNewGoal] = useState({ team_id: '', player_id: '', assist1_player_id: '', assist2_player_id: '', time: '', is_power_play: false });
  const [newPenalty, setNewPenalty] = useState({ team_id: '', player_id: '', duration: '', type: '', time: '' });
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null); // { type, dbId }

  useEffect(() => {
    if (!socket) return;
    socket.emit('joinMatchRoom', match.id);
    socket.emit('getMatchDetails', match.id);

    socket.on('matchGoals', (goals) => {
      setGoals(goals);
    });

    socket.on('matchPenalties', (penalties) => {
      setPenalties(penalties);
    });

    return () => {
      socket.emit('leaveMatchRoom', match.id);
      socket.off('matchGoals');
      socket.off('matchPenalties');
    };
  }, [match.id, socket]);

  const updateStatus = (status) => {
    if (!socket) return;
    socket.emit('updateMatchStatus', { id: match.id, status });
  };

  const handleGoalChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'team_id') {
      setNewGoal({
        ...newGoal,
        team_id: value,
        player_id: '',
        assist1_player_id: '',
        assist2_player_id: '',
      });
      return;
    }

    if (name === 'player_id') {
      setNewGoal({
        ...newGoal,
        player_id: value,
        assist1_player_id: newGoal.assist1_player_id === value ? '' : newGoal.assist1_player_id,
        assist2_player_id: newGoal.assist2_player_id === value ? '' : newGoal.assist2_player_id,
      });
      return;
    }

    if (name === 'assist1_player_id') {
      setNewGoal({
        ...newGoal,
        assist1_player_id: value,
        assist2_player_id: newGoal.assist2_player_id === value ? '' : newGoal.assist2_player_id,
      });
      return;
    }

    if (name === 'assist2_player_id') {
      setNewGoal({
        ...newGoal,
        assist2_player_id: value,
        assist1_player_id: newGoal.assist1_player_id === value ? '' : newGoal.assist1_player_id,
      });
      return;
    }

    setNewGoal({ ...newGoal, [name]: type === 'checkbox' ? checked : value });
  };

  const addGoal = (e) => {
    e.preventDefault();
    if (!socket) return;
    if (!newGoal.team_id) {
      alert('Valitse maalintekijän joukkue.');
      return;
    }
    if (!newGoal.player_id) {
      alert('Valitse maalintekijä.');
      return;
    }
    const teamPlayers = getTeamPlayers(newGoal.team_id);
    const scorerValid = teamPlayers.some((p) => Number(p.id) === Number(newGoal.player_id));
    if (!scorerValid) {
      alert('Maalintekijän täytyy kuulua valittuun joukkueeseen.');
      return;
    }
    const assistIds = [newGoal.assist1_player_id, newGoal.assist2_player_id].filter(Boolean);
    for (const assistId of assistIds) {
      if (!teamPlayers.some((p) => Number(p.id) === Number(assistId))) {
        alert('Syöttäjien täytyy kuulua valittuun joukkueeseen.');
        return;
      }
    }
    socket.emit('addGoal', { ...newGoal, match_id: match.id });
    setNewGoal({ team_id: '', player_id: '', assist1_player_id: '', assist2_player_id: '', time: '', is_power_play: false });
  };

  const handlePenaltyChange = (e) => {
    setNewPenalty({ ...newPenalty, [e.target.name]: e.target.value });
  };

  const addPenalty = (e) => {
    e.preventDefault();
    if (!socket) return;
    socket.emit('addPenalty', { ...newPenalty, match_id: match.id });
    setNewPenalty({ team_id: '', player_id: '', duration: '', type: '', time: '' });
  };

  const startEditEvent = (type, eventId) => {
    if (type === 'goal') {
      const goal = goals.find((g) => Number(g.id) === Number(eventId));
      if (!goal) return;
      setEditingEvent({ type, id: eventId });
      setEventForm({
        id: goal.id,
        team_id: goal.team_id,
        player_id: goal.player_id,
        assist1_player_id: goal.assist1_player_id || '',
        assist2_player_id: goal.assist2_player_id || '',
        time: goal.time || '',
        is_power_play: Boolean(goal.is_power_play),
      });
      return;
    }
    const penalty = penalties.find((p) => Number(p.id) === Number(eventId));
    if (!penalty) return;
    setEditingEvent({ type, id: eventId });
    setEventForm({
      id: penalty.id,
      team_id: penalty.team_id,
      player_id: penalty.player_id,
      duration: penalty.duration || '',
      type: penalty.type || '',
      time: penalty.time || '',
    });
  };

  const cancelEdit = () => {
    setEditingEvent(null);
    setEventForm({});
  };

  const handleEventFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEventForm({
      ...eventForm,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const saveEventUpdate = (e) => {
    e.preventDefault();
    if (!editingEvent || !socket) return;
    if (editingEvent.type === 'goal') {
      socket.emit('updateGoal', {
        id: eventForm.id,
        assist1_player_id: eventForm.assist1_player_id || null,
        assist2_player_id: eventForm.assist2_player_id || null,
        time: eventForm.time,
        is_power_play: eventForm.is_power_play,
      });
    } else {
      socket.emit('updatePenalty', {
        id: eventForm.id,
        duration: eventForm.duration,
        type: eventForm.type,
        time: eventForm.time,
      });
    }
    cancelEdit();
  };

  const deleteTimelineEvent = (type, eventId) => {
    if (!socket) return;
    if (type === 'goal') {
      socket.emit('deleteGoal', eventId);
    } else {
      socket.emit('deletePenalty', eventId);
    }
    if (editingEvent && editingEvent.id === eventId) {
      cancelEdit();
    }
    setPendingDelete(null);
  };

  const getTeamPlayers = (teamId) => {
    return players
      .filter(p => Number(p.team_id) === Number(teamId))
      .slice()
      .sort((a, b) => {
        const aNum = Number(a.number || 0);
        const bNum = Number(b.number || 0);
        if (aNum !== bNum) return aNum - bNum;
        return a.name.localeCompare(b.name);
      });
  };

  const getAssistPlayers = (teamId, excludedIds = []) => {
    const excludedSet = new Set(excludedIds.filter(Boolean).map((id) => String(id)));
    return getTeamPlayers(teamId).filter((p) => !excludedSet.has(String(p.id)));
  };

  const parseTimeToSeconds = (time) => {
    if (!time) return 0;
    const parts = String(time).split(':').map((part) => Number(part));
    if (parts.some(Number.isNaN)) return 0;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0] || 0;
  };

  const getTeamName = (teamId) => {
    if (Number(teamId) === Number(match.home_team_id)) return match.home_team_name;
    if (Number(teamId) === Number(match.away_team_id)) return match.away_team_name;
    const team = teams.find((t) => Number(t.id) === Number(teamId));
    return team ? team.name : 'Tuntematon joukkue';
  };

  const formatMatchStatus = (status) => {
    switch (status) {
      case 'scheduled':
        return 'Aikataulutettu';
      case 'live':
        return 'Käynnissä';
      case 'finished':
        return 'Päättynyt';
      default:
        return status;
    }
  };

  const timelineEvents = [
    ...goals.map((goal, index) => ({
      id: `goal-${goal.id || index}`,
      dbId: goal.id,
      type: 'goal',
      time: goal.time || '',
      seconds: parseTimeToSeconds(goal.time),
      teamName: getTeamName(goal.team_id),
      description: `${goal.player_name} teki maalin${goal.is_power_play ? ' (ylivoimalla)' : ''}${goal.assist1_name ? `, syöttäjä: ${goal.assist1_name}` : ''}${goal.assist2_name ? `, 2. syöttäjä: ${goal.assist2_name}` : ''}`,
    })),
    ...penalties.map((penalty, index) => ({
      id: `penalty-${penalty.id || index}`,
      dbId: penalty.id,
      type: 'penalty',
      time: penalty.time || '',
      seconds: parseTimeToSeconds(penalty.time),
      teamName: getTeamName(penalty.team_id),
      description: `${penalty.player_name} - ${penalty.type}, ${penalty.duration} min`,
    })),
  ].sort((a, b) => {
    if (a.seconds !== b.seconds) return a.seconds - b.seconds;
    if (a.type !== b.type) return a.type.localeCompare(b.type);
    return a.id.localeCompare(b.id);
  });

  const positionOrder = (pos) => {
    const p = (pos || '').trim().toUpperCase();
    if (p === 'G' || p === 'GOALIE' || p === 'GOALKEEPER') return 0;
    if (p === 'D' || p === 'DEFENSE' || p === 'DEFENSEMAN') return 1;
    if (p === 'F' || p === 'FORWARD' || p === 'LW' || p === 'RW' || p === 'C' || p === 'CENTER') return 2;
    return 3;
  };

  const sortRoster = (roster) =>
    roster.slice().sort((a, b) => {
      const posDiff = positionOrder(a.position) - positionOrder(b.position);
      if (posDiff !== 0) return posDiff;
      return Number(a.number || 0) - Number(b.number || 0);
    });

  const homePlayers = sortRoster(getTeamPlayers(match.home_team_id));
  const awayPlayers = sortRoster(getTeamPlayers(match.away_team_id));
  const [activeTab, setActiveTab] = useState('timeline');
  const canEdit = user && (user.role === 'SuperAdmin' || user.role === 'Admin' || user.role === 'AdminTournament' || user.role === 'MatchEditor');

  const handleBackClick = () => {
    if (location.state?.matchId && window.history.length > 1) {
      navigate(-1);
      return;
    }
    if (typeof back === 'function') {
      back();
      return;
    }
    navigate('/matches');
  };

  if (!match) {
    return <div>Valitse ottelu nähdäksesi tiedot.</div>;
  }

  return (
    <div>
      <div className="match-header-row">
        <button onClick={handleBackClick}>Takaisin otteluihin</button>
        <div className="match-status-row">
          <span className="match-status">Tila: {formatMatchStatus(match.status)}</span>
          {canEdit && match.status === 'scheduled' && (
            <button className="status-action-button" onClick={() => updateStatus('live')}>Aloita ottelu</button>
          )}
          {canEdit && match.status === 'live' && (
            <button className="status-action-button" onClick={() => updateStatus('finished')}>Lopeta ottelu</button>
          )}
        </div>
      </div>
      <div className="match-header">
        <div className="match-teams">
          <span className="match-team-name">{match.home_team_name}</span>
          <span className="match-vs">VS</span>
          <span className="match-team-name">{match.away_team_name}</span>
        </div>
        <div className="match-score">
          <span className="match-score-number">{match.home_score}</span>
          <span className="match-score-separator">-</span>
          <span className="match-score-number">{match.away_score}</span>
        </div>
      </div>
      <div className="match-tabs">
        <button
          type="button"
          className={`tab-button ${activeTab === 'timeline' ? 'active' : ''}`}
          onClick={() => setActiveTab('timeline')}
        >
          Tapahtumat
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'roster' ? 'active' : ''}`}
          onClick={() => setActiveTab('roster')}
        >
          Kokoonpanot
        </button>
      </div>

      {activeTab === 'roster' && (
        <div className="match-rosters">
          <div className="team-roster">
            <h3>{match.home_team_name}</h3>
            {homePlayers.length > 0 ? (
              <RosterByPosition players={homePlayers} />
            ) : (
              <p className="team-no-players">Tälle joukkueelle ei ole kokoonpanoa.</p>
            )}
          </div>
          <div className="team-roster">
            <h3>{match.away_team_name}</h3>
            {awayPlayers.length > 0 ? (
              <RosterByPosition players={awayPlayers} />
            ) : (
              <p className="team-no-players">Tälle joukkueelle ei ole kokoonpanoa.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'timeline' && (
        <>
          {editingEvent && editingEvent.type === 'goal' && (
            <section className="timeline-edit-form">
              <h3>Muokkaa maalia</h3>
              <form onSubmit={saveEventUpdate}>
                <div className="form-row">
                  <label>Aika</label>
                  <input type="text" name="time" value={eventForm.time} onChange={handleEventFormChange} placeholder="24:15" />
                </div>
                <div className="form-row">
                  <label>Syöttäjä 1</label>
                  <select name="assist1_player_id" value={eventForm.assist1_player_id} onChange={handleEventFormChange}>
                    <option value="">Ei syöttäjää</option>
                    {getAssistPlayers(parseInt(eventForm.team_id), [eventForm.player_id, eventForm.assist2_player_id]).map((p) => (
                      <option key={p.id} value={p.id}>{`${p.number ? `#${p.number} ` : ''}${p.name}`}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Syöttäjä 2</label>
                  <select name="assist2_player_id" value={eventForm.assist2_player_id} onChange={handleEventFormChange}>
                    <option value="">Ei syöttäjää</option>
                    {getAssistPlayers(parseInt(eventForm.team_id), [eventForm.player_id, eventForm.assist1_player_id]).map((p) => (
                      <option key={p.id} value={p.id}>{`${p.number ? `#${p.number} ` : ''}${p.name}`}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row checkbox-row">
                  <label>
                    <input type="checkbox" name="is_power_play" checked={eventForm.is_power_play} onChange={handleEventFormChange} />
                    Ylivoima
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit">Tallenna</button>
                  <button type="button" onClick={cancelEdit}>Peruuta</button>
                </div>
              </form>
            </section>
          )}
          {editingEvent && editingEvent.type === 'penalty' && (
            <section className="timeline-edit-form">
              <h3>Muokkaa rangaistusta</h3>
              <form onSubmit={saveEventUpdate}>
                <div className="form-row">
                  <label>Tyyppi</label>
                  <input type="text" name="type" value={eventForm.type} onChange={handleEventFormChange} />
                </div>
                <div className="form-row">
                  <label>Kesto</label>
                  <input type="number" name="duration" value={eventForm.duration} onChange={handleEventFormChange} />
                </div>
                <div className="form-row">
                  <label>Aika</label>
                  <input type="text" name="time" value={eventForm.time} onChange={handleEventFormChange} />
                </div>
                <div className="form-actions">
                  <button type="submit">Tallenna</button>
                  <button type="button" onClick={cancelEdit}>Peruuta</button>
                </div>
              </form>
            </section>
          )}
          <section className="timeline-section">
            <h3>Ottelutapahtumat</h3>
            {timelineEvents.length > 0 ? (
            <ul className="timeline-list">
              {timelineEvents.map((event) => (
                <li key={event.id} className={`timeline-event timeline-${event.type}`}>
                  <span className="timeline-time">{event.time || '00:00'}</span>
                  <span className="timeline-label">{event.type === 'goal' ? 'Maali' : 'Rangaistus'}</span>
                  <span className="timeline-team">{event.teamName}</span>
                  <span className="timeline-description">{event.description}</span>
                  {canEdit && (
                    <span className="timeline-actions">
                      <button type="button" onClick={(e) => { e.stopPropagation(); startEditEvent(event.type, event.dbId); }}>Muokkaa</button>
                      {pendingDelete && pendingDelete.dbId === event.dbId && pendingDelete.type === event.type ? (
                        <>
                          <span className="delete-confirm-text">Poistetaanko tapahtuma?</span>
                          <button type="button" className="confirm-yes" onClick={(e) => { e.stopPropagation(); deleteTimelineEvent(pendingDelete.type, pendingDelete.dbId); }}>Kyllä</button>
                          <button type="button" className="confirm-no" onClick={(e) => { e.stopPropagation(); setPendingDelete(null); }}>Ei</button>
                        </>
                      ) : (
                        <button type="button" onClick={(e) => { e.stopPropagation(); setPendingDelete({ type: event.type, dbId: event.dbId }); }}>Poista</button>
                      )}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>Tapahtumia ei ole vielä kirjattu.</p>
          )}
        </section>
        </>
      )}

      <div className="match-content">
        <div className="goals-section">
          <h3>Maalit</h3>
          {canEdit && (
            <form onSubmit={addGoal}>
              <select name="team_id" value={newGoal.team_id} onChange={handleGoalChange}>
                <option value="">Valitse joukkue</option>
                <option value={match.home_team_id}>{match.home_team_name}</option>
                <option value={match.away_team_id}>{match.away_team_name}</option>
              </select>
              <select name="player_id" value={newGoal.player_id} onChange={handleGoalChange} disabled={!newGoal.team_id}>
                <option value="">Valitse maalintekijä</option>
                {getTeamPlayers(parseInt(newGoal.team_id)).map(p => (
                  <option key={p.id} value={p.id}>{`${p.number ? `#${p.number} ` : ''}${p.name}`}</option>
                ))}
              </select>
              <select name="assist1_player_id" value={newGoal.assist1_player_id} onChange={handleGoalChange} disabled={!newGoal.team_id}>
                <option value="">Syöttäjä 1</option>
                {getAssistPlayers(parseInt(newGoal.team_id), [newGoal.player_id, newGoal.assist2_player_id]).map(p => (
                  <option key={p.id} value={p.id}>{`${p.number ? `#${p.number} ` : ''}${p.name}`}</option>
                ))}
              </select>
              <select name="assist2_player_id" value={newGoal.assist2_player_id} onChange={handleGoalChange} disabled={!newGoal.team_id}>
                <option value="">Syöttäjä 2</option>
                {getAssistPlayers(parseInt(newGoal.team_id), [newGoal.player_id, newGoal.assist1_player_id]).map(p => (
                  <option key={p.id} value={p.id}>{`${p.number ? `#${p.number} ` : ''}${p.name}`}</option>
                ))}
              </select>
              <input type="text" name="time" value={newGoal.time} onChange={handleGoalChange} placeholder="Aika (esim. 24:15)" />
              <label>
                <input type="checkbox" name="is_power_play" checked={newGoal.is_power_play} onChange={handleGoalChange} />
                Ylivoima
              </label>
              <button type="submit">Lisää maali</button>
            </form>
          )}
          <ul>
            {goals.map(goal => (
              <li key={goal.id}>{goal.player_name} ({goal.time})</li>
            ))}
          </ul>
        </div>

        <div className="penalties-section">
          <h3>Rangaistukset</h3>
          {canEdit && (
            <form onSubmit={addPenalty}>
              <select name="team_id" value={newPenalty.team_id} onChange={handlePenaltyChange}>
                <option value="">Valitse joukkue</option>
                <option value={match.home_team_id}>{match.home_team_name}</option>
                <option value={match.away_team_id}>{match.away_team_name}</option>
              </select>
              <select name="player_id" value={newPenalty.player_id} onChange={handlePenaltyChange}>
                <option value="">Valitse pelaaja</option>
                {getTeamPlayers(parseInt(newPenalty.team_id)).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input type="number" name="duration" value={newPenalty.duration} onChange={handlePenaltyChange} placeholder="Kesto (min)" />
              <input type="text" name="type" value={newPenalty.type} onChange={handlePenaltyChange} placeholder="Rangaistuksen tyyppi" />
              <input type="text" name="time" value={newPenalty.time} onChange={handlePenaltyChange} placeholder="Aika (esim. 24:15)" />
              <button type="submit">Lisää rangaistus</button>
            </form>
          )}
          <ul>
            {penalties.map(penalty => (
              <li key={penalty.id}>{penalty.player_name} - {penalty.type} ({penalty.duration} min) at {penalty.time}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Match;
